<template>
  <main class="founder-info">
    <div class="founder-info__header">
      <div class="founder-info__header-content">
        <h1 class="founder-info__title">Founder Information</h1>
        <p class="founder-info__subtitle">Manage your company founders</p>
      </div>
      <button
        v-if="organisationDetails?.founders?.length > 0"
        class="founder-info__add-btn"
        @click="showFounderForm = true"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M8 3.33334V12.6667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M3.33334 8H12.6667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        Add Founder
      </button>
    </div>

    <div class="founder-info__content" v-if="organisationDetails?.founders?.length > 0">
      <div class="founder-info__list">
        <div
          class="founder-info__card"
          v-for="founder in founders"
          :key="founder._id"
        >
          <div class="founder-info__card-avatar">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div class="founder-info__card-details">
            <h3 class="founder-info__card-name">{{ founder.fname }} {{ founder.lname }}</h3>
            <p class="founder-info__card-email">{{ founder.email }}</p>
          </div>
          <button
            class="founder-info__card-delete"
            @click="removeFounder(founder._id)"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M2.25 4.5H3.75H15.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M6 4.5V3C6 2.60218 6.15804 2.22064 6.43934 1.93934C6.72064 1.65804 7.10218 1.5 7.5 1.5H10.5C10.8978 1.5 11.2794 1.65804 11.5607 1.93934C11.842 2.22064 12 2.60218 12 3V4.5M14.25 4.5V15C14.25 15.3978 14.092 15.7794 13.8107 16.0607C13.5294 16.342 13.1478 16.5 12.75 16.5H5.25C4.85218 16.5 4.47064 16.342 4.18934 16.0607C3.90804 15.7794 3.75 15.3978 3.75 15V4.5H14.25Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </div>

    <div class="founder-info__empty" v-else>
      <div class="founder-info__empty-icon">
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
          <path d="M40 42V38C40 35.8783 39.1571 33.8434 37.6569 32.3431C36.1566 30.8429 34.1217 30 32 30H16C13.8783 30 11.8434 30.8429 10.3431 32.3431C8.84285 33.8434 8 35.8783 8 38V42" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M24 22C28.4183 22 32 18.4183 32 14C32 9.58172 28.4183 6 24 6C19.5817 6 16 9.58172 16 14C16 18.4183 19.5817 22 24 22Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <h2 class="founder-info__empty-title">No founders added yet</h2>
      <p class="founder-info__empty-desc">Add information about your company founders</p>
      <button
        class="founder-info__empty-btn"
        @click="showFounderForm = true"
      >
        Add Founder Information
      </button>
    </div>

    <AddFounderModal
      v-if="showFounderForm"
      title="Add Founder"
      subTitle="Enter founder details"
      @close="showFounderForm = false"
      :show="true"
      :bgChange="true"
      @go-back="showFounderForm = false"
    >
      <template v-slot:content>
        <form @submit.prevent="validateForm = true" class="founder-info__form">
          <div class="founder-info__form-header">
            <div class="founder-info__form-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <h2 class="founder-info__form-title">Founder Information</h2>
            <p class="founder-info__form-desc">Enter the founder's details below</p>
          </div>

          <div class="founder-info__form-group">
            <label class="founder-info__form-label">First Name</label>
            <InputField
              label="Enter first name"
              :validate="validateForm"
              type="text"
              name="fname"
              :currentValue="founder.fname"
              @input-changed="storeInfo"
              @is-valid="handleValidation"
            />
          </div>

          <div class="founder-info__form-group">
            <label class="founder-info__form-label">Last Name</label>
            <InputField
              label="Enter last name"
              :validate="validateForm"
              type="text"
              name="lname"
              :currentValue="founder.lname"
              @input-changed="storeInfo"
              @is-valid="handleValidation"
            />
          </div>

          <div class="founder-info__form-group">
            <label class="founder-info__form-label">Email Address</label>
            <InputField
              label="Enter email address"
              :validate="validateForm"
              type="email"
              name="email"
              :currentValue="founder.email"
              @input-changed="storeInfo"
              @is-valid="handleValidation"
            />
          </div>

          <div class="founder-info__form-actions">
            <button type="button" class="founder-info__form-cancel" @click="showFounderForm = false">
              Cancel
            </button>
            <CustomButton title="Add Founder" />
          </div>
        </form>
      </template>
    </AddFounderModal>
  </main>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import AddFounderModal from "@/components/general/sidemodal/SideModal";
import InputField from "@/components/general/InputField";
import CustomButton from "@/components/general/BtnComponent";

export default {
  name: "FounderInfo",
  data() {
    return {
      showFounderForm: false,
      founders: [
        {
          fname: "",
          lname: "",
          email: "",
        },
      ],
      founder: {
        fname: "",
        lname: "",
        email: "",
      },
      validateForm: false,
      validationResult: [],
    };
  },
  components: {
    AddFounderModal,
    InputField,
    CustomButton,
  },
  computed: {
    ...mapGetters("organisationModule", ["organisationDetails"]),
  },
  watch: {
    organisationDetails(val) {
      if (val && val.founders && val.founders.length > 0) {
        this.founders = val.founders;
      }
    },
    validationResult: {
      deep: true,
      handler(val) {
        if (
          val.length >= Object.keys(this.founder).length &&
          !val.includes(false)
        ) {
          this.addFounder();
        }
      },
    },
    validateForm(val) {
      if (val) {
        setTimeout(() => {
          this.validateForm = false;
        }, 1000);
      }
    },
  },
  created() {
    this.getOrganisationProfile();
  },
  methods: {
    ...mapActions("organisationModule", [
      "getOrganisationProfile",
      "updateOrgDetails",
      "deleteOrgFounder",
    ]),
    ...mapActions(["showToast"]),
    handleValidation(value) {
      this.validationResult.push(value);
    },
    storeInfo(object) {
      this.founder[object.name] = object.value;
    },
    async addFounder() {
      const founders = [this.founder];
      try {
        const response = await this.updateOrgDetails({ founders });
        const { message } = response.data;
        this.showToast({
          description: message,
          display: true,
          type: "success",
        });
        this.showFounderForm = false;
        this.getOrganisationProfile();
      } catch (error) {
        return error;
      }
    },
    async removeFounder(id) {
      try {
        const response = await this.deleteOrgFounder(id);
        const { message } = response.data;
        this.showToast({
          description: message,
          display: true,
          type: "success",
        });
        this.getOrganisationProfile();
      } catch (error) {
        return error;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.founder-info {
  background: #FFFFFF;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  min-height: 100%;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 24px;
    border-bottom: 1px solid #E2E8F0;
  }

  &__header-content {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__title {
    font-family: 'DM Sans', sans-serif;
    font-size: 18px;
    font-weight: 500;
    color: #0F172A;
    margin: 0;
  }

  &__subtitle {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #64748B;
    margin: 0;
  }

  &__add-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    background: #4D84FF;
    color: white;
    border: none;
    border-radius: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s ease;

    &:hover {
      background: #3D6FE6;
    }
  }

  &__content {
    padding: 24px;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__card {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px;
    background: #F8FAFC;
    border: 1px solid #E2E8F0;
    border-radius: 10px;
    transition: all 0.2s ease;

    &:hover {
      border-color: #4D84FF;
    }
  }

  &__card-avatar {
    width: 48px;
    height: 48px;
    background: #E2E8F0;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #64748B;
    flex-shrink: 0;
  }

  &__card-details {
    flex: 1;
    min-width: 0;
  }

  &__card-name {
    font-family: 'DM Sans', sans-serif;
    font-size: 15px;
    font-weight: 500;
    color: #0F172A;
    margin: 0 0 4px 0;
  }

  &__card-email {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #64748B;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__card-delete {
    width: 36px;
    height: 36px;
    background: transparent;
    border: 1px solid #E2E8F0;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #64748B;
    cursor: pointer;
    transition: all 0.2s ease;
    flex-shrink: 0;

    &:hover {
      background: #FEE2E2;
      border-color: #FECACA;
      color: #EF4444;
    }
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 24px;
    text-align: center;
  }

  &__empty-icon {
    width: 80px;
    height: 80px;
    background: #F1F5F9;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #64748B;
    margin-bottom: 24px;
  }

  &__empty-title {
    font-family: 'DM Sans', sans-serif;
    font-size: 18px;
    font-weight: 500;
    color: #0F172A;
    margin: 0 0 8px 0;
  }

  &__empty-desc {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #64748B;
    margin: 0 0 24px 0;
    max-width: 280px;
  }

  &__empty-btn {
    padding: 12px 24px;
    background: #4D84FF;
    color: white;
    border: none;
    border-radius: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s ease;

    &:hover {
      background: #3D6FE6;
    }
  }

  &__form {
    padding: 0 24px 24px;
    max-width: 320px;
    margin: 0 auto;
  }

  &__form-header {
    text-align: center;
    margin-bottom: 32px;
  }

  &__form-icon {
    width: 56px;
    height: 56px;
    background: #F1F5F9;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
    color: #64748B;
  }

  &__form-title {
    font-family: 'DM Sans', sans-serif;
    font-size: 18px;
    font-weight: 500;
    color: #0F172A;
    margin: 0 0 8px 0;
  }

  &__form-desc {
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #64748B;
    margin: 0;
  }

  &__form-group {
    margin-bottom: 20px;
  }

  &__form-label {
    display: block;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #0F172A;
    margin-bottom: 8px;
  }

  &__form-actions {
    display: flex;
    gap: 12px;
    margin-top: 32px;
  }

  &__form-cancel {
    flex: 1;
    padding: 12px;
    background: transparent;
    border: 1px solid #E2E8F0;
    border-radius: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #64748B;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: #F8FAFC;
      border-color: #CBD5E1;
    }
  }
}
</style>
